import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import BaseTable from "../Base/BaseTable";
import BookingRow from "./BookingRow";
import BookingModal from "./BookingModal";
import AddBookingModal from "./AddBookingModal";
import CalendarFilter from "./CalendarFilter";
import {
  getBookings,
  updateFullBooking,
  deleteBooking,
} from "../../../../api/bookingApi";
import { BOOKING_STATUS_OPTIONS } from "../../../../constants/enums";
import { translateBookingStatus } from "../../../../utils/stranslateUltils";

const BookingTable = () => {
  const { t } = useTranslation();
  /** -------------------- STATE -------------------- **/
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");

  const STATUS_OPTIONS = BOOKING_STATUS_OPTIONS.map(option => ({
    value: option.value,
    label: option.value
      ? translateBookingStatus(t, option.value)  
      : t("all")                  
  }));  
/** -------------------- API HANDLERS -------------------- **/
  const loadBookings = useCallback(async (page, size, keyword, status, date) => {
    try {
      setLoading(true);
      setError(null);

      const searchParams = {
        page: page - 1,
        size: size,
        date: null
      };

      if (keyword && keyword.trim()) {
        searchParams.keyword = keyword.trim();
      }

      if (status && status !== "" && status !== "all") {
        searchParams.status = status;
      }

      if (date && date.trim()) {
        searchParams.date = date.trim();
      }

      const response = await getBookings(searchParams);
      setCurrentPage(page);
      setPageSize(size);
      setSearchTerm(keyword || "");
      setStatusFilter(status || "");
      setDateFilter(date || "");

      if (response && Array.isArray(response.content)) {
        setBookings(response.content);
        setTotalPages(response.totalPages);
        setTotalItems(response.totalElements);
      } else {
        setBookings([]);
        setTotalPages(0);
        setTotalItems(0);
      }
    } catch (err) {
      setError("Failed to load bookings. Please try again later.");
      console.error("Error loading bookings:", err);
      setBookings([]);
      setTotalPages(0);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateBookingHandler = useCallback(async (updatedBooking) => {
    const response = await updateFullBooking(updatedBooking.bookingId, updatedBooking);
    setBookings((prev) =>
      prev.map((b) => (b.bookingId === response.bookingId ? response : b))
    );
    return response;
  }, []);

  const deleteBookingHandler = useCallback(async (booking) => {
    await deleteBooking(booking.bookingId);
    
    const nextTotal = totalItems - 1;
    const nextTotalPages = Math.ceil(Math.max(nextTotal, 0) / pageSize);
    const targetPage = nextTotalPages > 0 ? Math.min(currentPage, nextTotalPages) : 1;

    setBookings((prev) => prev.filter((b) => b.bookingId !== booking.bookingId));
    setTotalItems(Math.max(nextTotal, 0));
    setTotalPages(nextTotalPages);

    if (targetPage !== currentPage) {
      await loadBookings(targetPage, pageSize, searchTerm, statusFilter, dateFilter);
    }
  }, [pageSize, currentPage, totalItems, searchTerm, statusFilter, dateFilter, loadBookings]);

  const handleAddBookingSuccess = useCallback(() => {
    loadBookings(currentPage, pageSize, searchTerm, statusFilter, dateFilter);
  }, [currentPage, pageSize, searchTerm, statusFilter, dateFilter, loadBookings]);

  /** -------------------- RENDER FUNCTIONS -------------------- **/
  const renderRow = (booking, actions) => (
    <BookingRow
      key={booking.bookingId}
      booking={booking}
      onShowDetails={actions.onShowDetails}
      onEdit={actions.onEdit}
      onDelete={actions.onDelete}
    />
  );

  const renderModal = (booking, mode, onSave, onDelete, onClose) => (
    <BookingModal
      booking={booking}
      mode={mode}
      onSave={onSave}
      onDelete={onDelete}
      onClose={onClose}
    />
  );

  const renderAddModal = (onClose, onSuccess) => (
    <AddBookingModal
      onClose={onClose}
      onSuccess={onSuccess}
    />
  );

  // Initial load
  React.useEffect(() => {
    loadBookings(1, 10, "", "", "");
  }, [loadBookings]);

  return (
    <BaseTable
      title={t('admin.bookings.title')}
      subtitle={t('admin.bookings.subtitle')}
      items={bookings}
      loading={loading}
      error={error}
      currentPage={currentPage}
      totalPages={totalPages}
      totalItems={totalItems}
      pageSize={pageSize}
      searchTerm={searchTerm}
      statusFilter={statusFilter}
      dateFilter={dateFilter}
      columns={[
        t('admin.bookings.table.booking_id'),
        t('admin.bookings.table.customer'),
        t('admin.bookings.table.phone'),
        t('admin.bookings.table.booking_date'),
        t('admin.bookings.table.status'),
        ""
      ]}
      renderRow={renderRow}
      renderModal={renderModal}
      renderAddModal={renderAddModal}
      statusOptions={STATUS_OPTIONS}
      onLoadItems={loadBookings}
      onUpdateItem={updateBookingHandler}
      onDeleteItem={deleteBookingHandler}
      onAddItem={handleAddBookingSuccess}
      showDateFilter={true}
      DateFilterComponent={CalendarFilter}
    />
  );
};

export default BookingTable;